import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";

export default function Articles() {
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        api.get("/articles")
            .then(({ data }) => setArticles(data))
            .catch(() => setArticles([]));
    }, []);

    return (
        <div data-testid="articles-page" className="bg-xv-ivory">
            <section className="pt-32 md:pt-44 pb-16 md:pb-24">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">Articles</span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-black text-[48px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.98] tracking-tight max-w-5xl"
                            style={{ fontWeight: 300 }}
                        >
                            Notes d'analyses
                            <br />
                            <em className="not-italic text-xv-midnight">
                                pour les dirigeants exigeants.
                            </em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-12 max-w-2xl text-xv-text-secondary text-[17px] md:text-[19px] leading-[1.8] font-light">
                            Une lecture sobre, rigoureuse, écrite pour
                            ceux qui préfèrent la profondeur à la
                            tendance.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-28 md:pb-40">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12">
                    <div className="border-t border-xv-line">
                        {articles.map((a, i) => (
                            <Reveal key={a.slug || i} delay={i * 0.06}>
                                <Link
                                    to={`/articles/${a.slug}`}
                                    data-testid={`article-row-${i}`}
                                    className="group grid md:grid-cols-12 gap-6 md:gap-10 items-center py-10 md:py-14 border-b border-xv-line transition-colors duration-500 hover:bg-xv-cream/40 px-2 md:px-4"
                                >
                                    <div className="md:col-span-2">
                                        <div className="overline text-xv-gold">
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className="overline text-xv-text-secondary mt-2">
                                            {a.kicker}
                                        </div>
                                    </div>
                                    <div className="md:col-span-6">
                                        <h2
                                            className="font-serif text-3xl md:text-4xl lg:text-[44px] text-xv-black leading-[1.1] transition-colors duration-500 group-hover:text-xv-midnight"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {a.title}
                                        </h2>
                                        <p className="mt-4 text-xv-text-secondary text-[15px] md:text-[16px] leading-[1.85] font-light max-w-2xl">
                                            {a.excerpt}
                                        </p>
                                        <div className="mt-5 flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-xv-gold/80">
                                            <img
                                                src={a.author_photo || "/brand/joel-kotho-avatar.jpg"}
                                                alt={a.author || "Joel Kotho"}
                                                className="w-8 h-8 rounded-full object-cover border border-xv-gold/40"
                                            />
                                            Par {a.author || "Joel Kotho"}
                                            {a.readTime ? (
                                                <span className="text-xv-text-secondary/60 normal-case tracking-normal text-[12px]">
                                                    · {a.readTime}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="md:col-span-3 hidden md:block overflow-hidden">
                                        {a.image && (
                                            <img
                                                src={a.image}
                                                alt={a.title}
                                                className="w-full h-[180px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                                            />
                                        )}
                                    </div>
                                    <div className="md:col-span-1 flex justify-end">
                                        <ArrowUpRight
                                            strokeWidth={1.2}
                                            size={24}
                                            className="text-xv-midnight transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                        />
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                        {articles.length === 0 && (
                            <div className="py-24 text-center text-xv-text-secondary font-light">
                                Aucun article publié pour le moment.
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
